// Module 09 §9 — the public dashboard-list surface, and the dashboard-only LANDING TARGET that
// 05's `permissionGuard` redirects to (ledger B9).
//
// The seam has three owners and no cycle: 07 owns the FLAG (`useRbac().onlyDashboard`), 09 owns
// the TARGET (`firstDashboardRoute`) and the PREDICATE (`isDashboardRoute`), and 05 composes
// them. That is why this file reads 07 through its barrel and imports nothing from `app/`.
import { computed, type ComputedRef } from 'vue'

import type { DashboardDefinitionDto } from '../models/api.types'
import {
  firstDashboardRoute as resolveFirstDashboardRoute,
  type DashboardRouteTarget,
} from '../service/dashboardRoute'
import { useDashboardStore } from '../stores/dashboardStore'

import { useAccount, useWorkspace } from '@/features/account'

/** Loose id compare — the wire mixes string and number ids for the same workspace. */
function sameWorkspace(
  a: number | string | null | undefined,
  b: number | string | null | undefined,
): boolean {
  return a != null && b != null && String(a) === String(b)
}

export interface UseDashboards {
  definitions: ComputedRef<DashboardDefinitionDto[]>
  /** `definitions` narrowed to the currently selected workspace — what a listing surface shows. */
  workspaceDefinitions: ComputedRef<DashboardDefinitionDto[]>
  accountCenters: ComputedRef<DashboardDefinitionDto[]>
  hasAnyDashboard: ComputedRef<boolean>
  /** Where an `onlyDashboard` user lands. `CustomDashboardCreate` when there is no dashboard. */
  firstDashboardRoute: ComputedRef<DashboardRouteTarget>
  dashboardByUrl: (url: string) => DashboardDefinitionDto | null
  accountCenterByUrl: (url: string) => DashboardDefinitionDto | null
  /** The fan-out fallback + the SSE/resource-sync refresh entry point (see below). */
  loadDefinitions: (force?: boolean) => Promise<void>
  loadAccountCenters: (force?: boolean) => Promise<void>
}

/** Every `workspace_id` under `userAccessInfo.dashboards[].items` (v3 `dashboardStore.ts:107-109`). */
function accessWorkspaceIds(dashboards: unknown[] | undefined): (number | string)[] {
  const ids: (number | string)[] = []
  for (const group of dashboards ?? []) {
    const items = (group as { items?: unknown }).items
    if (!Array.isArray(items)) continue
    for (const item of items) {
      const id = (item as { workspace_id?: number | string }).workspace_id
      if (id) ids.push(id)
    }
  }
  return ids
}

export function useDashboards(): UseDashboards {
  const store = useDashboardStore()
  const account = useAccount()
  const workspace = useWorkspace()

  const definitions = computed(() => store.definitions)
  const workspaceDefinitions = computed(() =>
    definitions.value.filter((dashboard) =>
      sameWorkspace(dashboard.workspace_id, workspace.workspaceId.value),
    ),
  )

  return {
    definitions,
    workspaceDefinitions,
    accountCenters: computed(() => store.accountCenters),
    hasAnyDashboard: computed(() => store.hasAnyDashboard),
    // The resolver is pure and takes its fallback ids explicitly, so the reactive wiring is
    // exactly this computed — 05 reads `.value` inside the guard and gets a fresh target.
    firstDashboardRoute: computed(() =>
      resolveFirstDashboardRoute(definitions.value, {
        accountId: account.accountId.value ?? null,
        workspaceId: workspace.workspaceId.value ?? null,
        workspaceAccountId: workspace.workspace.value?.account_id ?? null,
      }),
    ),
    dashboardByUrl: (url) => store.dashboardByUrl(url),
    accountCenterByUrl: (url) => store.accountCenterByUrl(url),
    /**
     * Also the refresh the shell-owned SSE resource-sync bus calls for the `dashboards` resource
     * (v3 `useSSENotifications.js:93` → `getCustomDashboards(true)`). Contract §6 claims module 09
     * "never opens its own event stream" AND that a bus exists to subscribe to; the first half
     * holds here, the second does not yet — `shared/http`'s `openEventStream` is on `dev` but no
     * resource-sync bus is, so there is nothing to subscribe to. This is the entry point that bus
     * calls when it lands; see the PR body (BE-09-09).
     */
    loadDefinitions: (force = false) =>
      store.loadDefinitions({
        workspaceId: workspace.workspaceId.value ?? null,
        accessWorkspaceIds: accessWorkspaceIds(account.userAccessInfo.value.dashboards),
        force,
      }),
    loadAccountCenters: (force = false) =>
      store.loadAccountCenters(account.accountId.value ?? null, force),
  }
}
